import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { ArrowRight, ShoppingBag, Store, Clock, DollarSign } from 'lucide-react';
import { mockStores, mockOrders } from '../utils/mockData';
import { calculateStoreStatus, getStatusColor } from '../utils/storeStatus';
import { Link, useNavigate } from 'react-router-dom';
export function Dashboard() {
  const navigate = useNavigate();
  const [orderFilter, setOrderFilter] = useState<string>('all');
  const stores = mockStores.map(store => ({
    ...store,
    status: calculateStoreStatus(store)
  }));
  const openStores = stores.filter(s => s.status === 'open').length;
  const scheduledStores = stores.filter(s => s.status === 'scheduled').length;
  const closedStores = stores.filter(s => s.status === 'closed').length;
  const totalOrders = 234;
  const closingSoonStores = stores.filter(s => s.status === 'closing-soon' || s.status === 'open' && new Date(s.settings.closeDate).getTime() - Date.now() < 72 * 3600000);
  // Filter recent orders
  const filteredOrders = orderFilter === 'all' ? mockOrders.slice(0, 5) : mockOrders.filter(o => o.status === orderFilter).slice(0, 5);
  const orderStatuses = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'received',
    label: 'Received'
  }, {
    value: 'in-production',
    label: 'In Production'
  }, {
    value: 'packed',
    label: 'Packed'
  }, {
    value: 'shipped',
    label: 'Shipped'
  }];
  return <div className="space-y-6" data-id="element-593">
      <div className="flex justify-between items-center" data-id="element-594">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-595">Dashboard</h1>
        <Link to="/pop-up-stores/new" data-id="element-596">
          <Button data-id="element-597">Create New Store</Button>
        </Link>
      </div>

      {/* KPI Cards - Now Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-598">
        <button onClick={() => navigate('/pop-up-stores?status=open')} className="text-left" data-id="element-599">
          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" data-id="element-600">
            <div className="flex items-center" data-id="element-601">
              <div className="p-3 rounded-full bg-green-100 text-green-600" data-id="element-602">
                <Store className="h-6 w-6" data-id="element-603" />
              </div>
              <div className="ml-4" data-id="element-604">
                <p className="text-sm font-medium text-gray-500" data-id="element-605">Open Stores</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-606">{openStores}</p>
              </div>
            </div>
          </Card>
        </button>

        <button onClick={() => navigate('/pop-up-stores?status=scheduled')} className="text-left" data-id="element-607">
          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" data-id="element-608">
            <div className="flex items-center" data-id="element-609">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600" data-id="element-610">
                <Clock className="h-6 w-6" data-id="element-611" />
              </div>
              <div className="ml-4" data-id="element-612">
                <p className="text-sm font-medium text-gray-500" data-id="element-613">Scheduled</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-614">
                  {scheduledStores}
                </p>
              </div>
            </div>
          </Card>
        </button>

        <button onClick={() => navigate('/pop-up-stores?status=closed&range=30d')} className="text-left" data-id="element-615">
          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" data-id="element-616">
            <div className="flex items-center" data-id="element-617">
              <div className="p-3 rounded-full bg-red-100 text-red-600" data-id="element-618">
                <Store className="h-6 w-6" data-id="element-619" />
              </div>
              <div className="ml-4" data-id="element-620">
                <p className="text-sm font-medium text-gray-500" data-id="element-621">
                  Closed (30d)
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-622">
                  {closedStores}
                </p>
              </div>
            </div>
          </Card>
        </button>

        <button onClick={() => navigate('/orders?range=30d')} className="text-left" data-id="element-623">
          <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer" data-id="element-624">
            <div className="flex items-center" data-id="element-625">
              <div className="p-3 rounded-full bg-gray-100 text-gray-600" data-id="element-626">
                <ShoppingBag className="h-6 w-6" data-id="element-627" />
              </div>
              <div className="ml-4" data-id="element-628">
                <p className="text-sm font-medium text-gray-500" data-id="element-629">
                  Total Orders (30d)
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-630">
                  {totalOrders}
                </p>
              </div>
            </div>
          </Card>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-631">
        {/* Stores Closing Soon - Now with clickable rows */}
        <Card title="Stores Closing Soon" action={<Link to="/pop-up-stores?status=closing-soon" className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-633">
              View all →
            </Link>} data-id="element-632">
          {closingSoonStores.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-634">
              No stores closing soon
            </div> : <div className="space-y-2" data-id="element-635">
              {closingSoonStores.map(store => <div key={store.id} onClick={() => navigate(`/pop-up-stores/${store.id}`)} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" data-id="element-636">
                  <div className="flex-1" data-id="element-637">
                    <p className="font-medium text-gray-900" data-id="element-638">{store.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-639">
                      Closes{' '}
                      {new Date(store.settings.closeDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right mr-4" data-id="element-640">
                    <p className="text-sm font-medium text-gray-900" data-id="element-641">
                      {store.stats.ordersCount} orders
                    </p>
                  </div>
                  <button onClick={e => {
              e.stopPropagation();
              navigate(`/pop-up-stores/${store.id}`);
            }} className="text-red-600 hover:text-red-700 text-sm font-medium" data-id="element-642">
                    View Store →
                  </button>
                </div>)}
            </div>}
        </Card>

        {/* Recent Orders - Now with filter chips and clickable rows */}
        <Card title="Recent Orders" action={<Link to="/orders" className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-644">
              View all →
            </Link>} data-id="element-643">
          {/* Filter Chips */}
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-645">
            {orderStatuses.map(status => <button key={status.value} onClick={() => setOrderFilter(status.value)} className={`
                  px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${orderFilter === status.value ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                `} data-id="element-646">
                {status.label}
              </button>)}
          </div>

          {/* Orders List */}
          <div className="space-y-2" data-id="element-647">
            {filteredOrders.map(order => <div key={order.id} onClick={() => navigate(`/orders/${order.id}`)} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" data-id="element-648">
                <div className="flex-1" data-id="element-649">
                  <p className="font-medium text-gray-900" data-id="element-650">
                    #{order.id.split('_')[1]}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-651">{order.customerName}</p>
                </div>
                <div className="text-right mr-4" data-id="element-652">
                  <p className="font-medium text-gray-900" data-id="element-653">
                    ${order.total.toFixed(2)}
                  </p>
                  <Badge variant={order.status === 'completed' ? 'green' : 'blue'} className="mt-1" data-id="element-654">
                    {order.status.replace('-', ' ')}
                  </Badge>
                </div>
              </div>)}
          </div>
        </Card>
      </div>
    </div>;
}